<script setup>
import { useI18n } from "vue-i18n";
import { FormCard, FormListbox, FormSwitch, FormCheckboxGroupOption, FormCheckboxGroup, FormModelListbox, FormTextarea } from "@Components/Form";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronUpIcon, ImageIcon, TriangleAlertIcon } from "lucide-vue-next";
import { computed } from "vue";
import { writingStyles } from "@Utils/persona";
import ProfilePart from "./ProfilePart.vue";
import CustomFieldsPart from "./CustomFieldsPart.vue";
import BlocksPart from "./BlocksPart.vue";
import { models as textModels, getProvider as getTextProvider, getGroup as getTextGroup } from "@Utils/text-models";
import { models as imagesModels, getProvider as getImageProvider } from "@Utils/image-models";
import _map from "lodash/map";
import _groupBy from "lodash/groupBy";
import _cloneDeep from "lodash/cloneDeep";

const props = defineProps(["modelValue", "personas", "templates", "statuses", "categories", "postTypes", "users"]);
defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();

const formattedWritingStyles = computed(() => {
    return writingStyles.map((s) => {
        return {
            key: s.key,
            name: __(s.name),
        };
    });
});

const hasImageBlock = computed(() => {
    return props.modelValue.modules.content.blocks.some((block) => block.block === "image");
});

const textModelsGroups = _map(_groupBy(_cloneDeep(textModels), "group"), (items, group) => {
    return {
        name: getTextGroup(group).name,
        items: items,
    };
});

const imagesModelsGroups = _map(_groupBy(_cloneDeep(imagesModels), "provider"), (items, provider) => {
    return {
        name: getImageProvider(provider).name,
        items: items,
    };
});
</script>

<template>
    <FormCard>
        <div class="space-y-6">
            <div class="p-6 bg-gray-50 rounded-lg dark:bg-gray-900">
                <div class="flex gap-3">
                    <TriangleAlertIcon class="w-5 h-5 text-yellow-600 dark:text-yellow-500" />

                    <div>
                        <div class="text-sm font-medium leading-5 text-yellow-600 dark:text-yellow-500">{{ __("Attention") }}</div>
                        <div class="mt-1 text-sm">
                            {{ __("Le mode avancé devient obsolète. Il sera automatiquement désactivé dans les prochaines versions.") }}
                        </div>
                    </div>
                </div>
            </div>

            <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
                <DisclosureButton
                    :class="[
                        open ? 'rounded-t-lg' : 'rounded-lg',
                        'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                    ]">
                    <span>{{ __("Profil de rédacteur") }}</span>
                    <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
                </DisclosureButton>
                <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                    <ProfilePart :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" :profiles="personas" />
                </DisclosurePanel>
            </Disclosure>

            <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
                <DisclosureButton
                    :class="[
                        open ? 'rounded-t-lg' : 'rounded-lg',
                        'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                    ]">
                    <span>{{ __("Options du texte") }}</span>
                    <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
                </DisclosureButton>
                <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                    <FormModelListbox v-model="modelValue.modules.models.text" :groups="textModelsGroups" required :label="__('Modèle de texte IA')">
                        <template v-slot:details="{ item }">
                            <div class="flex flex-col gap-3 p-3">
                                <div>
                                    <span class="text-xs text-gray-600 dark:text-gray-400">{{ getTextProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                    <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                                </div>
                                <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                                <div v-if="item.quality">
                                    <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                    <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                        <div
                                            v-for="i in 10"
                                            :key="i"
                                            :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                            class="h-1 rounded-full"></div>
                                    </div>
                                </div>
                                <div v-if="item.speed">
                                    <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                    <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                        <div
                                            v-for="i in 10"
                                            :key="i"
                                            :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                            class="h-1 rounded-full"></div>
                                    </div>
                                </div>
                                <div v-if="item.price">
                                    <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                    <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/1M tokens</span>
                                </div>
                            </div>
                        </template>
                    </FormModelListbox>
                    <div v-if="modelValue.type === 'news' || modelValue.type === 'news-now'" class="space-y-3">
                        <FormSwitch v-model="modelValue.modules.content.sources.enabled">
                            {{ __("Ajouter la source") }}
                        </FormSwitch>

                        <div v-if="modelValue.modules.content.sources.enabled" class="ml-7 space-y-3">
                            <FormSwitch :z="1" v-model="modelValue.modules.content.sources.no_follow">
                                {{ __("No-follow") }}
                            </FormSwitch>
                            <FormSwitch :z="1" v-model="modelValue.modules.content.sources.blank">
                                {{ __("Blank") }}
                            </FormSwitch>
                        </div>
                    </div>

                    <div class="max-w-xs">
                        <FormListbox
                            :label="__('Style d\'écriture')"
                            :items="formattedWritingStyles"
                            itemKey="key"
                            v-model="modelValue.modules.content.writing_style" />
                    </div>

                    <FormSwitch
                        v-model="modelValue.modules.content.automatic.keywords.automatic.enabled"
                        :label="__('Générer et intégrer des mots clés en relation automatiquement')" />
                </DisclosurePanel>
            </Disclosure>

            <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
                <DisclosureButton
                    :class="[
                        open ? 'rounded-t-lg' : 'rounded-lg',
                        'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                    ]">
                    <span>{{ __("Paramétrez vos blocks pour personnaliser votre contenu.") }}</span>
                    <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
                </DisclosureButton>
                <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                    <BlocksPart :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" />
                </DisclosurePanel>
            </Disclosure>

            <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
                <DisclosureButton
                    :class="[
                        open ? 'rounded-t-lg' : 'rounded-lg',
                        'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                    ]">
                    <span>{{ __("Médias") }}</span>
                    <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
                </DisclosureButton>
                <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                    <FormModelListbox v-model="modelValue.modules.models.image" :groups="imagesModelsGroups" required :label="__('Modèle d\'images')">
                        <template v-slot:details="{ item }">
                            <div class="flex flex-col gap-3 p-3">
                                <div>
                                    <span class="text-xs text-gray-600 dark:text-gray-400">{{ getImageProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                    <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                                </div>
                                <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                                <div v-if="item.quality">
                                    <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                    <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                        <div
                                            v-for="i in 10"
                                            :key="i"
                                            :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                            class="h-1 rounded-full"></div>
                                    </div>
                                </div>
                                <div v-if="item.speed">
                                    <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                    <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                        <div
                                            v-for="i in 10"
                                            :key="i"
                                            :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                            class="h-1 rounded-full"></div>
                                    </div>
                                </div>
                                <div v-if="item.price">
                                    <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                    <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/image</span>
                                </div>
                            </div>
                        </template>
                    </FormModelListbox>
                    <div>
                        <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            {{ __("Intégration des médias") }}
                        </div>
                        <FormCheckboxGroup>
                            <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                                <FormCheckboxGroupOption
                                    v-model="modelValue.modules.wordpress.thumbnail.enabled"
                                    :title="__('Image mise en avant')"
                                    :icon="ImageIcon"
                                    :tooltip="__('Générer une image de mise en avant')"
                                    size="small" />
                            </div>
                        </FormCheckboxGroup>
                    </div>

                    <FormTextarea
                        v-if="modelValue.modules.wordpress.thumbnail.enabled || hasImageBlock"
                        v-model="modelValue.modules.content.custom_instructions.images"
                        :label="__('Instructions personnalisées pour les images')"
                        :placeholder="__('Exemple : Créer des images avec un style noir et blanc')"
                        :rows="5"
                        :left-actions="[
                            {
                                id: 'magic-prompt',
                            },
                        ]">
                    </FormTextarea>
                </DisclosurePanel>
            </Disclosure>
        </div>

        <!-- Custom fields -->
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Champs personnalisés") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <CustomFieldsPart :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" />
            </DisclosurePanel>
        </Disclosure>
    </FormCard>
</template>
